/**
 * Open items: what the bookkeeping still expects to see on a bank account.
 * Derived – never stored – from `verifikationer.meta` and the `bankRows`
 * links, so the legal record stays the only source of truth:
 *
 *   settlement        a booking on a bank account (1930 …) with no bank row
 *                     linked yet (a receipt booked before the statement arrived)
 *   supplier_invoice  a purchase booked against 2440 that no `settle` has paid
 *   customer_invoice  a sale booked against 1510 that no `settle` has paid
 *   reimbursement     a purchase paid with the owner's money (2893) that the
 *                     company has not paid back yet – closed by a `settle`
 *                     booking against it (the payback D 2893 / K 1930) or by
 *                     a bank row linked to it
 *
 * `amountOre` is signed like the bank row it expects: negative = money out.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { OpenItem } from '../inbox/types';
import type { Ore } from '../money';
import { formatSek } from '../money';
import { formatSwedishDate } from '../periods/deadlines';
import { CUSTOMER_INVOICE_ACCOUNT, SUPPLIER_INVOICE_ACCOUNT } from '../transactions/compile';
import type { BankRow, InboxItem, IsoDate, PaymentSource, Uuid, Verifikation } from '../types';
import { addDays, daysBetween, DEFAULT_PAYMENT_TERM_DAYS, type RefundablePurchase } from './match';

/** Owner account whose credit balance the company is expected to pay back (AB, förening, BRF). */
export const REIMBURSABLE_OWNER_ACCOUNT = 2893;
/** Days after which an unsettled receipt prompts "Betalade du kanske privat?". */
export const STALE_SETTLEMENT_DAYS = 30;

export interface OpenItemsSource {
  verifikationer: readonly Verifikation[];
  bankRows: readonly Pick<BankRow, 'linkedVerifikationIds'>[];
  paymentSources: readonly PaymentSource[];
  /** Verifikationer whose receipt was in a foreign currency (from the inbox item's reading). */
  foreignCurrencyIds?: ReadonlySet<Uuid> | undefined;
}

/** Bank account numbers per bank-kind payment source (card/swish resolve to their bank source). */
export function bankAccountNumbers(sources: readonly PaymentSource[]): Set<number> {
  const byId = new Map(sources.map((s) => [s.id, s]));
  const out = new Set<number>();
  for (const s of sources) {
    switch (s.kind) {
      case 'bank':
        out.add(s.accountNumber);
        break;
      case 'card':
      case 'swish': {
        const linked = s.linkedBankSourceId ? byId.get(s.linkedBankSourceId) : undefined;
        out.add(linked?.accountNumber ?? s.accountNumber);
        break;
      }
      case 'cash':
      case 'owner':
      case 'supplier_credit':
      case 'customer_credit':
      case 'tax_account':
        break;
      default: {
        const exhaustive: never = s.kind;
        throw new Error(`Unknown payment source kind ${String(exhaustive)}`);
      }
    }
  }
  return out;
}

function netOn(v: Pick<Verifikation, 'rows'>, account: number): Ore {
  let net = 0;
  for (const r of v.rows) if (r.accountNumber === account) net += r.debit - r.credit;
  return net;
}

/** What a purchase paid with the owner's money still owes the owner (credit on 2893), 0 for anything else. */
export function reimbursementOwed(v: Pick<Verifikation, 'rows'>): Ore {
  const owed = -netOn(v, REIMBURSABLE_OWNER_ACCOUNT);
  return owed > 0 ? owed : 0;
}

/** True when `v` is an invoice booking a `settle` can pay (2440 credit or 1510 debit). */
export function isOpenInvoiceShape(v: Pick<Verifikation, 'rows'>): boolean {
  return v.rows.some((r) => (r.accountNumber === SUPPLIER_INVOICE_ACCOUNT && r.credit > 0) || (r.accountNumber === CUSTOMER_INVOICE_ACCOUNT && r.debit > 0));
}

/** First digit run of five or more in the description: an OCR or invoice number. */
export function referenceOf(description: string): string | undefined {
  return /\d{5,}/.exec(description)?.[0];
}

/** Pure derivation; see the module comment for the rules. */
export function deriveOpenItems(src: OpenItemsSource): OpenItem[] {
  const bankAccounts = bankAccountNumbers(src.paymentSources);
  const linked = new Set<Uuid>();
  for (const row of src.bankRows) for (const id of row.linkedVerifikationIds) linked.add(id);
  const corrected = new Set<Uuid>();
  const settled = new Map<Uuid, Ore>();
  for (const v of src.verifikationer) {
    if (v.correctsId) corrected.add(v.correctsId);
    if (v.meta?.settlesVerifikationId) {
      const paid = v.rows.reduce((s, r) => s + r.debit, 0);
      settled.set(v.meta.settlesVerifikationId, (settled.get(v.meta.settlesVerifikationId) ?? 0) + paid);
    }
  }

  const out: OpenItem[] = [];
  for (const v of src.verifikationer) {
    const meta = v.meta;
    if (!meta || v.correctsId || corrected.has(v.id)) continue;
    const counterparty = meta.counterparty;
    const foreignCurrency = src.foreignCurrencyIds?.has(v.id) === true;

    // Settlements: any bank account movement not yet backed by a bank row.
    const bookedFromBank = (meta.bankRowIds?.length ?? 0) > 0 || linked.has(v.id);
    if (!bookedFromBank) {
      for (const account of bankAccounts) {
        const net = netOn(v, account);
        if (net === 0) continue;
        out.push({ kind: 'settlement', verifikationId: v.id, ledgerAccount: account, amountOre: net, date: v.date, counterparty, ...(foreignCurrency ? { foreignCurrency } : {}) });
      }
    }

    // Invoices and reimbursements depend on the kind.
    switch (meta.kind) {
      case 'purchase': {
        const supplier = -netOn(v, SUPPLIER_INVOICE_ACCOUNT);
        if (supplier > 0) pushInvoice(out, v, 'supplier_invoice', SUPPLIER_INVOICE_ACCOUNT, -supplier, settled.get(v.id) ?? 0);
        const owner = reimbursementOwed(v);
        const remaining = owner - (settled.get(v.id) ?? 0);
        if (owner > 0 && remaining > 0 && !linked.has(v.id)) {
          out.push({ kind: 'reimbursement', verifikationId: v.id, ledgerAccount: REIMBURSABLE_OWNER_ACCOUNT, amountOre: -remaining, date: v.date, counterparty });
        }
        break;
      }
      case 'sale': {
        const customer = netOn(v, CUSTOMER_INVOICE_ACCOUNT);
        if (customer > 0) pushInvoice(out, v, 'customer_invoice', CUSTOMER_INVOICE_ACCOUNT, customer, settled.get(v.id) ?? 0);
        break;
      }
      case 'refund':
      case 'transfer':
      case 'owner_in':
      case 'owner_out':
      case 'tax':
      case 'settle':
      case 'vat_settlement':
      case 'year_end':
      case 'other':
        break;
      default: {
        const exhaustive: never = meta.kind;
        throw new Error(`Unknown kind ${String(exhaustive)}`);
      }
    }
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

function pushInvoice(out: OpenItem[], v: Verifikation, kind: 'supplier_invoice' | 'customer_invoice', account: number, signedGross: Ore, paidOre: Ore): void {
  const remaining = Math.abs(signedGross) - paidOre;
  if (remaining <= 0) return;
  const reference = referenceOf(v.description);
  out.push({
    kind,
    verifikationId: v.id,
    ledgerAccount: account,
    amountOre: Math.sign(signedGross) * remaining,
    date: v.date,
    dueDate: addDays(v.date, DEFAULT_PAYMENT_TERM_DAYS),
    counterparty: v.meta?.counterparty ?? '',
    ...(reference ? { reference } : {}),
  });
}

/**
 * Booked purchases that money back from the merchant, or a credit note, could
 * still reduce: the gross of the purchase minus the refunds already booked
 * against it (`meta.kind === 'refund'` with `settlesVerifikationId`). With
 * `paidFromBank` only purchases that left a bank account count — a refund
 * arriving on the account can only mirror a payment that went out of it; a
 * credit note may also reduce an invoice that is still unpaid.
 */
export function refundablePurchases(src: Pick<OpenItemsSource, 'verifikationer' | 'paymentSources'>, opts: { paidFromBank: boolean }): RefundablePurchase[] {
  const bankAccounts = bankAccountNumbers(src.paymentSources);
  const corrected = new Set<Uuid>();
  const refunded = new Map<Uuid, Ore>();
  for (const v of src.verifikationer) {
    if (v.correctsId) corrected.add(v.correctsId);
    if (v.meta?.kind === 'refund' && v.meta.settlesVerifikationId) {
      const gross = v.rows.reduce((s, r) => s + r.debit, 0);
      refunded.set(v.meta.settlesVerifikationId, (refunded.get(v.meta.settlesVerifikationId) ?? 0) + gross);
    }
  }
  const out: RefundablePurchase[] = [];
  for (const v of src.verifikationer) {
    const meta = v.meta;
    if (!meta || meta.kind !== 'purchase' || v.correctsId || corrected.has(v.id)) continue;
    if (opts.paidFromBank) {
      let fromBank = false;
      for (const account of bankAccounts) if (netOn(v, account) < 0) fromBank = true;
      if (!fromBank) continue;
    }
    const gross = v.rows.reduce((s, r) => s + r.debit, 0);
    const remainingOre = gross - (refunded.get(v.id) ?? 0);
    if (remainingOre <= 0) continue;
    out.push({ verifikationId: v.id, remainingOre, date: v.date, counterparty: meta.counterparty });
  }
  return out.sort((a, b) => b.date.localeCompare(a.date) || a.verifikationId.localeCompare(b.verifikationId));
}

/** Foreign-currency verifikationer, looked up through their inbox items. */
export function foreignCurrencyIdsFrom(items: readonly Pick<InboxItem, 'verifikationId' | 'reading'>[]): Set<Uuid> {
  const out = new Set<Uuid>();
  for (const it of items) {
    if (it.verifikationId && it.reading && it.reading.currency.toUpperCase() !== 'SEK') out.add(it.verifikationId);
  }
  return out;
}

/** Everything a company's bank accounts still owe the bookkeeping an explanation for. */
export async function openItemsFor(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<OpenItem[]> {
  const [verifikationer, bankRows, paymentSources, items] = await Promise.all([
    db.verifikationer.where('companyId').equals(companyId).toArray(),
    db.bankRows.where('companyId').equals(companyId).toArray(),
    db.paymentSources.where('companyId').equals(companyId).toArray(),
    db.inboxItems.where('companyId').equals(companyId).and((i) => !!i.verifikationId).toArray(),
  ]);
  return deriveOpenItems({ verifikationer, bankRows, paymentSources, foreignCurrencyIds: foreignCurrencyIdsFrom(items) });
}

/** Settlements older than `days` (default 30): the "Betalade du kanske privat?" prompt list. */
export function staleSettlements(items: readonly OpenItem[], today: IsoDate, days: number = STALE_SETTLEMENT_DAYS): OpenItem[] {
  return items.filter((i) => i.kind === 'settlement' && i.amountOre < 0 && daysBetween(i.date, today) >= days);
}

/** "Kvittot på 625,00 kr från Clas Ohlson den 4 mars har inte synts på företagskontot. Betalade du kanske privat?" */
export function staleSettlementPrompt(item: OpenItem, today: IsoDate): { text: string; options: { label: string; value: 'private' | 'wait' }[] } {
  const merchant = item.counterparty || 'säljaren';
  return {
    text: `Kvittot på ${formatSek(Math.abs(item.amountOre))} kr från ${merchant} den ${formatSwedishDate(item.date, { relativeTo: today })} har inte synts på företagskontot. Betalade du kanske privat?`,
    options: [
      { label: 'Ja', value: 'private' },
      { label: 'Nej, vänta', value: 'wait' },
    ],
  };
}

/** The inbox item fields that say "this receipt was linked to that booking later". */
export type DocumentLinkItem = Pick<InboxItem, 'kind' | 'status' | 'attachmentId' | 'verifikationId'>;

/**
 * Verifikationer booked without a document ("Bokför utan kvitto") that still
 * lack one. `meta.missingDocument` is immutable, so a receipt uploaded later
 * resolves it through its inbox item (kind receipt, booked, with a file,
 * pointing at the verifikation) – pass the company's items to see that.
 */
export function missingDocumentIds(verifikationer: readonly Verifikation[], items: readonly DocumentLinkItem[] = []): Set<Uuid> {
  const corrected = new Set(verifikationer.filter((v) => v.correctsId).map((v) => v.correctsId));
  const documented = new Set<Uuid>();
  for (const it of items) {
    if (it.kind === 'receipt' && it.status === 'booked' && it.attachmentId && it.verifikationId) documented.add(it.verifikationId);
  }
  const out = new Set<Uuid>();
  for (const v of verifikationer) {
    if (!v.meta?.missingDocument || v.correctsId || corrected.has(v.id)) continue;
    if (v.attachmentIds.length > 0 || documented.has(v.id)) continue;
    out.add(v.id);
  }
  return out;
}

/** Number of booked purchases that still lack a document ("2 köp saknar kvitto"). */
export function missingDocumentCount(verifikationer: readonly Verifikation[], items: readonly DocumentLinkItem[] = []): number {
  return missingDocumentIds(verifikationer, items).size;
}

/** The company's bookings that still lack a document, oldest first (badge, filter and year-end blocker). */
export async function missingDocumentsFor(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<Verifikation[]> {
  const [verifikationer, items] = await Promise.all([
    db.verifikationer.where('companyId').equals(companyId).toArray(),
    db.inboxItems.where('companyId').equals(companyId).and((i) => !!i.verifikationId).toArray(),
  ]);
  const ids = missingDocumentIds(verifikationer, items);
  return verifikationer.filter((v) => ids.has(v.id)).sort((a, b) => a.date.localeCompare(b.date) || a.number - b.number);
}
